import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  AdminApiError,
  listAdminDepartments,
  listAdminRequestTypes,
  listCompanies,
  type CompanyResponse,
  type DepartmentAdminResponse,
  type RequestTypeAdminResponse,
} from "../../api/admin";
import "./MasterDataPage.css";
import DepartmentCreatePanel from "./DepartmentCreatePanel";
import DepartmentEditPanel from "./DepartmentEditPanel";
import DepartmentLifecyclePanel, { type DepartmentLifecycleAction } from "./DepartmentLifecyclePanel";
import RequestCategoryCreatePanel from "./RequestCategoryCreatePanel";
import RequestCategoryEditPanel from "./RequestCategoryEditPanel";
import RequestCategoryLifecyclePanel, { type RequestCategoryLifecycleAction } from "./RequestCategoryLifecyclePanel";

type TabKey = "departments" | "request-categories";

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

type LoadReason = "initial" | "refresh";

const TAB_ORDER: readonly TabKey[] = ["departments", "request-categories"];

function safeErrorMessage(error: unknown): string {
  if (error instanceof AdminApiError && error.detail) {
    return error.detail;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Unable to load master data. Please try again.";
}

function isInternalCompany(company: CompanyResponse): boolean {
  return company.type === "Internal" && company.is_active;
}

function nextTab(tab: TabKey): TabKey {
  const index = TAB_ORDER.indexOf(tab);
  return TAB_ORDER[(index + 1) % TAB_ORDER.length];
}

function previousTab(tab: TabKey): TabKey {
  const index = TAB_ORDER.indexOf(tab);
  return TAB_ORDER[(index - 1 + TAB_ORDER.length) % TAB_ORDER.length];
}

export default function MasterDataPage() {
  const [tab, setTab] = useState<TabKey>("departments");
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [companies, setCompanies] = useState<CompanyResponse[]>([]);
  const [departments, setDepartments] = useState<DepartmentAdminResponse[]>([]);
  const [requestTypes, setRequestTypes] = useState<RequestTypeAdminResponse[]>([]);
  const [notice, setNotice] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);
  const [editingDepartmentId, setEditingDepartmentId] = useState<number | null>(null);
  const isMountedRef = useRef(true);
  const editButtonRefs = useRef(new Map<number, HTMLButtonElement | null>());
  const pendingEditFocusRef = useRef<number | null>(null);
  const [lifecycleSelection, setLifecycleSelection] = useState<{ id: number; action: DepartmentLifecycleAction } | null>(null);
  const lifecycleButtonRefs = useRef(new Map<number, HTMLButtonElement | null>());
  const pendingLifecycleFocusRef = useRef<number | null>(null);
  const [editingRequestTypeId, setEditingRequestTypeId] = useState<number | null>(null);
  const requestTypeEditButtonRefs = useRef(new Map<number, HTMLButtonElement | null>());
  const pendingRequestTypeEditFocusRef = useRef<number | null>(null);
  const [requestTypeLifecycleSelection, setRequestTypeLifecycleSelection] = useState<{ id: number; action: RequestCategoryLifecycleAction } | null>(null);
  const requestTypeLifecycleButtonRefs = useRef(new Map<number, HTMLButtonElement | null>());
  const pendingRequestTypeLifecycleFocusRef = useRef<number | null>(null);
  const pendingRequestTypeLifecycleFocusActionRef = useRef<"deactivate" | "reactivate" | null>(null);
  const [isRequestCategoryCreateOpen, setIsRequestCategoryCreateOpen] = useState(false);
  const hasBootstrappedRef = useRef(false);
  const departmentsTabRef = useRef<HTMLButtonElement | null>(null);
  const requestCategoriesTabRef = useRef<HTMLButtonElement | null>(null);

  const loadAll = useCallback(async (reason: LoadReason) => {
    const isInitialLoad = reason === "initial";
    setState({ kind: "loading" });
    if (isInitialLoad) {
      setNotice("Loading master data.");
    } else {
      setNotice("Refreshing master data.");
    }
    try {
      const [companyList, departmentList, requestTypeList] = await Promise.all([
        listCompanies(),
        listAdminDepartments(true),
        listAdminRequestTypes(true),
      ]);
      if (!isMountedRef.current) {
        return;
      }
      setCompanies(companyList);
      setDepartments(departmentList);
      setRequestTypes(requestTypeList);
      setState({ kind: "ready" });
      if (isInitialLoad) {
        setNotice("Master data loaded.");
      } else {
        setNotice("Master data refreshed.");
      }
      setHasLoaded(true);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      const message = safeErrorMessage(error);
      setState({ kind: "error", message });
      setNotice("");
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (hasBootstrappedRef.current) {
      return;
    }
    hasBootstrappedRef.current = true;
    loadAll("initial").catch(() => undefined);
  }, [loadAll]);


  const clearPageNotice = useCallback(() => {
    setNotice((current) => {
      if (current.length === 0) {
        return current;
      }
      if (
        current === "Loading master data." ||
        current === "Refreshing master data." ||
        current === "Master data loaded."
      ) {
        return current;
      }
      return "";
    });
  }, []);
  const handleRefresh = useCallback(() => {
    void loadAll("refresh");
  }, [loadAll]);

  const selectedEditDepartment =
    editingDepartmentId === null
      ? null
      : departments.find((department) => department.id === editingDepartmentId) ?? null;

  const selectedLifecycleDepartment =
    lifecycleSelection === null
      ? null
      : departments.find((department) => department.id === lifecycleSelection.id) ?? null;

  const selectedRequestType =
    editingRequestTypeId === null
      ? null
      : requestTypes.find((requestType) => requestType.id === editingRequestTypeId) ?? null;

  const selectedRequestTypeLifecycle =
    requestTypeLifecycleSelection === null
      ? null
      : requestTypes.find((requestType) => requestType.id === requestTypeLifecycleSelection.id) ?? null;

  useEffect(() => {
    if (
      editingDepartmentId !== null &&
      selectedEditDepartment === null &&
      state.kind === "ready"
    ) {
      pendingEditFocusRef.current = null;
      queueMicrotask(() => {
        setEditingDepartmentId(null);
      });
    }
  }, [editingDepartmentId, selectedEditDepartment, state.kind]);



  const handleEditOpen = useCallback((departmentId: number) => {
    clearPageNotice();
    pendingEditFocusRef.current = null;
    pendingLifecycleFocusRef.current = null;
    setLifecycleSelection(null);
    setEditingDepartmentId(departmentId);
  }, [clearPageNotice]);

  const handleEditClose = useCallback(() => {
    const currentId = editingDepartmentId;
    if (currentId !== null) {
      pendingEditFocusRef.current = currentId;
    }
    setEditingDepartmentId(null);
  }, [editingDepartmentId]);

  const handleLifecycleClose = useCallback(() => {
    setLifecycleSelection((prev) => {
      if (prev !== null) {
        pendingLifecycleFocusRef.current = prev.id;
      }
      return null;
    });
  }, []);

  const handleLifecycleOpen = useCallback((id: number, action: DepartmentLifecycleAction) => {
    clearPageNotice();
    pendingLifecycleFocusRef.current = null;
    pendingEditFocusRef.current = null;
    setEditingDepartmentId(null);
    setLifecycleSelection({ id, action });
  }, [clearPageNotice]);

  const handleLifecycleCompleted = useCallback(async () => {
    await loadAll("refresh");
  }, [loadAll]);

  const handleRequestTypeEditOpen = useCallback((requestTypeId: number) => {
    clearPageNotice();
    pendingRequestTypeEditFocusRef.current = null;
    pendingRequestTypeLifecycleFocusRef.current = null;
    setIsRequestCategoryCreateOpen(false);
    setRequestTypeLifecycleSelection(null);
    setEditingRequestTypeId(requestTypeId);
  }, [clearPageNotice]);

  const handleRequestTypeEditClose = useCallback(() => {
    const currentId = editingRequestTypeId;
    if (currentId !== null) {
      pendingRequestTypeEditFocusRef.current = currentId;
    }
    setEditingRequestTypeId(null);
  }, [editingRequestTypeId]);

  const handleRequestTypeLifecycleOpen = useCallback(
    (id: number, action: RequestCategoryLifecycleAction) => {
      clearPageNotice();
      pendingRequestTypeLifecycleFocusRef.current = null;
      pendingRequestTypeLifecycleFocusActionRef.current = null;
      pendingRequestTypeEditFocusRef.current = null;
      setIsRequestCategoryCreateOpen(false);
      setEditingRequestTypeId(null);
      setRequestTypeLifecycleSelection({ id, action });
    },
    [clearPageNotice],
  );

  const handleRequestTypeLifecycleClose = useCallback(() => {
    setRequestTypeLifecycleSelection((prev) => {
      if (prev !== null) {
        pendingRequestTypeLifecycleFocusRef.current = prev.id;
      }
      return null;
    });
  }, []);

  const handleRequestTypeLifecycleCompleted = useCallback(async () => {
    await loadAll("refresh");
  }, [loadAll]);

  const handleRequestCategoryCreateOpenChange = useCallback(
    (next: boolean) => {
      setIsRequestCategoryCreateOpen(next);
      if (next) {
        // Mutex: opening Create closes Edit and Lifecycle.
        setEditingRequestTypeId(null);
        setRequestTypeLifecycleSelection(null);
        pendingRequestTypeEditFocusRef.current = null;
        pendingRequestTypeLifecycleFocusRef.current = null;
      }
    },
    [],
  );

  const handleRequestCategoryCreated = useCallback(async () => {
    setIsRequestCategoryCreateOpen(false);
    await loadAll("refresh");
    setNotice("Request Category created.");
  }, [loadAll]);

  const handleRequestTypeUpdated = useCallback(async () => {
    await loadAll("refresh");
    setNotice("Request Category updated.");
  }, [loadAll]);

  const handleTabChange = useCallback((next: TabKey) => {
    setTab(next);
    if (next !== "departments") {
      setEditingDepartmentId(null);
      setLifecycleSelection(null);
      pendingEditFocusRef.current = null;
      pendingLifecycleFocusRef.current = null;
    }
    if (next !== "request-categories") {
      setEditingRequestTypeId(null);
      setRequestTypeLifecycleSelection(null);
      setIsRequestCategoryCreateOpen(false);
      pendingRequestTypeEditFocusRef.current = null;
      pendingRequestTypeLifecycleFocusRef.current = null;
    }
  }, []);

  const focusTab = useCallback((next: TabKey) => {
    const target = next === "departments" ? departmentsTabRef.current : requestCategoriesTabRef.current;
    target?.focus();
  }, []);

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (
        event.key !== "ArrowRight" &&
        event.key !== "ArrowLeft" &&
        event.key !== "Home" &&
        event.key !== "End"
      ) {
        return;
      }
      event.preventDefault();
      let next: TabKey;
      if (event.key === "ArrowRight") {
        next = nextTab(tab);
      } else if (event.key === "ArrowLeft") {
        next = previousTab(tab);
      } else if (event.key === "Home") {
        next = "departments";
      } else {
        next = "request-categories";
      }
      setTab(next);
      focusTab(next);
    },
    [tab, focusTab],
  );

  useEffect(() => {
    if (selectedEditDepartment !== null) {
      return;
    }
    const pending = pendingEditFocusRef.current;
    if (pending === null) {
      return;
    }
    if (!departments.some((department) => department.id === pending)) {
      pendingEditFocusRef.current = null;
      return;
    }
    if (state.kind === "loading") {
      return;
    }
    pendingEditFocusRef.current = null;
    const button = editButtonRefs.current.get(pending);
    button?.focus();
  }, [departments, selectedEditDepartment, state.kind]);

  useEffect(() => {
    if (lifecycleSelection === null) {
      return;
    }
    if (selectedLifecycleDepartment === null) {
      return;
    }
    if (state.kind !== "ready") {
      return;
    }
    const action = lifecycleSelection.action;
    const dept = selectedLifecycleDepartment;
    const refreshedMatchesRequest =
      (action === "deactivate" && dept.is_active === false) ||
      (action === "reactivate" && dept.is_active === true);
    if (!refreshedMatchesRequest) {
      return;
    }
    pendingLifecycleFocusRef.current = dept.id;
    const message =
      action === "deactivate" ? "Department deactivated." : "Department reactivated.";
    queueMicrotask(() => {
      setNotice(message);
      setLifecycleSelection(null);
    });
  }, [lifecycleSelection, selectedLifecycleDepartment, state.kind]);

  useEffect(() => {
    if (
      lifecycleSelection === null ||
      selectedLifecycleDepartment !== null ||
      state.kind !== "ready"
    ) {
      return;
    }
    pendingLifecycleFocusRef.current = null;
    queueMicrotask(() => {
      setLifecycleSelection(null);
    });
  }, [lifecycleSelection, selectedLifecycleDepartment, state.kind]);

  useEffect(() => {
    const pendingId = pendingLifecycleFocusRef.current;

    if (pendingId === null) {
      return;
    }

    if (lifecycleSelection !== null || state.kind !== "ready") {
      return;
    }

    const departmentExists = departments.some(
      (department) => department.id === pendingId,
    );

    if (!departmentExists) {
      pendingLifecycleFocusRef.current = null;
      return;
    }

    pendingLifecycleFocusRef.current = null;
    lifecycleButtonRefs.current.get(pendingId)?.focus();
  }, [departments, lifecycleSelection, state.kind]);

  useEffect(() => {
    if (selectedRequestType !== null) {
      return;
    }
    const pending = pendingRequestTypeEditFocusRef.current;
    if (pending === null) {
      return;
    }
    if (!requestTypes.some((requestType) => requestType.id === pending)) {
      pendingRequestTypeEditFocusRef.current = null;
      return;
    }
    if (state.kind === "loading") {
      return;
    }
    pendingRequestTypeEditFocusRef.current = null;
    const button = requestTypeEditButtonRefs.current.get(pending);
    button?.focus();
  }, [requestTypes, selectedRequestType, state.kind]);

  useEffect(() => {
    if (requestTypeLifecycleSelection === null) {
      return;
    }
    if (selectedRequestTypeLifecycle === null) {
      return;
    }
    if (state.kind !== "ready") {
      return;
    }
    const action = requestTypeLifecycleSelection.action;
    const rt = selectedRequestTypeLifecycle;
    const refreshedMatchesRequest =
      (action === "deactivate" && rt.is_active === false) ||
      (action === "reactivate" && rt.is_active === true);
    if (!refreshedMatchesRequest) {
      return;
    }
    pendingRequestTypeLifecycleFocusRef.current = rt.id;
    pendingRequestTypeLifecycleFocusActionRef.current = action;
    const message =
      action === "deactivate" ? "Request Category deactivated." : "Request Category reactivated.";
    queueMicrotask(() => {
      setNotice(message);
      setRequestTypeLifecycleSelection(null);
    });
  }, [requestTypeLifecycleSelection, selectedRequestTypeLifecycle, state.kind]);

  useEffect(() => {
    if (
      requestTypeLifecycleSelection === null ||
      selectedRequestTypeLifecycle !== null ||
      state.kind !== "ready"
    ) {
      return;
    }
    pendingRequestTypeLifecycleFocusRef.current = null;
    queueMicrotask(() => {
      setRequestTypeLifecycleSelection(null);
    });
  }, [requestTypeLifecycleSelection, selectedRequestTypeLifecycle, state.kind]);

  useEffect(() => {
    const pendingId = pendingRequestTypeLifecycleFocusRef.current;
    const pendingAction = pendingRequestTypeLifecycleFocusActionRef.current;
    if (pendingId === null) {
      return;
    }
    if (requestTypeLifecycleSelection !== null || state.kind !== "ready") {
      return;
    }
    const targetRequestType = requestTypes.find(
      (requestType) => requestType.id === pendingId,
    );
    if (targetRequestType === undefined) {
      pendingRequestTypeLifecycleFocusRef.current = null;
      pendingRequestTypeLifecycleFocusActionRef.current = null;
      return;
    }
    if (pendingAction !== null) {
      const expectedIsActive = pendingAction === "reactivate";
      if (targetRequestType.is_active !== expectedIsActive) {
        return;
      }
    }
    pendingRequestTypeLifecycleFocusRef.current = null;
    pendingRequestTypeLifecycleFocusActionRef.current = null;
    requestTypeLifecycleButtonRefs.current.get(pendingId)?.focus();
  }, [requestTypes, requestTypeLifecycleSelection, state.kind]);


  const isLoading = state.kind === "loading";
  const internalCompanies = companies.filter(isInternalCompany);
  const internalCompanyNames = internalCompanies.map((company) => company.name);
  const activeInternalCompanyIds = new Set(
    internalCompanies.filter((c) => c.is_active).map((c) => c.id),
  );
  const eligibleRoutingDepartments = departments.filter((department) => {
    if (!department.is_active || !department.is_ticket_routing_enabled) {
      return false;
    }
    return activeInternalCompanyIds.has(department.company_id);
  });
  const showInternalWarning = state.kind === "ready" && internalCompanies.length === 0;
  const showInitialLoading = state.kind === "loading" && !hasLoaded;
  return (
    <section className="master-data-page" aria-labelledby="master-data-title">
      <header className="master-data-page__header">
        <div>
          <h1 id="master-data-title" className="master-data-page__title">
            Master Data
          </h1>
          <p className="master-data-page__description">
            Admins manage routing Departments and Request Categories used by tickets across the pilot.
          </p>
        </div>
        <button
          type="button"
          className="master-data-page__refresh"
          onClick={handleRefresh}
          disabled={isLoading}
          aria-label={isLoading ? "Refreshing master data" : "Refresh master data"}
        >
          {isLoading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <section className="master-data-page__context" aria-label="Company context">
        <h2 className="master-data-page__context-title">Company context</h2>
        {internalCompanyNames.length > 0 ? (
          <p className="master-data-page__context-text">
            Internal company{internalCompanyNames.length === 1 ? "" : "ies"}:
            {" "}
            <strong>{internalCompanyNames.join(", ")}</strong>
          </p>
        ) : null}
        {showInternalWarning ? (
          <p className="master-data-page__context-warning" role="status">
            No active Internal company is currently configured. Routing Departments and Request Categories
            cannot be created until one exists.
          </p>
        ) : null}
      </section>

      <div className="master-data-page__tabs" role="tablist" aria-label="Master data sections">
        <button
          type="button"
          role="tab"
          id="master-data-tab-departments"
          aria-selected={tab === "departments"}
          aria-controls="master-data-panel-departments"
          tabIndex={tab === "departments" ? 0 : -1}
          ref={departmentsTabRef}
          className={`master-data-page__tab${tab === "departments" ? " master-data-page__tab--active" : ""}`}
          onClick={() => handleTabChange("departments")}
          onKeyDown={handleTabKeyDown}
        >
          Departments
        </button>
        <button
          type="button"
          role="tab"
          id="master-data-tab-request-categories"
          aria-selected={tab === "request-categories"}
          aria-controls="master-data-panel-request-categories"
          tabIndex={tab === "request-categories" ? 0 : -1}
          ref={requestCategoriesTabRef}
          className={`master-data-page__tab${tab === "request-categories" ? " master-data-page__tab--active" : ""}`}
          onClick={() => handleTabChange("request-categories")}
          onKeyDown={handleTabKeyDown}
        >
          Request Categories
        </button>
      </div>

      <div className="master-data-page__announcements" aria-live="polite" aria-atomic="true">
        {notice ? <p className="master-data-page__notice">{notice}</p> : null}
      </div>

      {state.kind === "error" ? (
        <section
          id={tab === "departments" ? "master-data-panel-departments" : "master-data-panel-request-categories"}
          role="tabpanel"
          aria-labelledby={tab === "departments" ? "master-data-tab-departments" : "master-data-tab-request-categories"}
        >
          <section role="alert" className="master-data-page__error">
            <p className="master-data-page__error-title">Unable to load master data</p>
            <p className="master-data-page__error-message">{state.message}</p>
            <button
              type="button"
              className="master-data-page__retry"
              onClick={handleRefresh}
            >
              Retry
            </button>
          </section>
        </section>
      ) : null}

      {state.kind !== "error" && tab === "departments" ? (
        <section
          id="master-data-panel-departments"
          role="tabpanel"
          aria-labelledby="master-data-tab-departments"
          className="master-data-page__panel"
        >
          {hasLoaded ? (
            <DepartmentCreatePanel
              companies={internalCompanies}
              disabled={isLoading}
              onCreated={() => loadAll("refresh")}
              onCreateOpen={clearPageNotice}
            />
          ) : null}
          {selectedEditDepartment !== null ? (
            <DepartmentEditPanel
              key={selectedEditDepartment.id}
              department={selectedEditDepartment}
              disabled={isLoading}
              onUpdated={() => loadAll("refresh")}
              onCancel={handleEditClose}
            />
          ) : null}
          {selectedLifecycleDepartment !== null && lifecycleSelection !== null ? (
            <DepartmentLifecyclePanel
              key={`lifecycle-${selectedLifecycleDepartment.id}-${lifecycleSelection.action}`}
              department={selectedLifecycleDepartment}
              action={lifecycleSelection.action}
              disabled={isLoading}
              onCompleted={handleLifecycleCompleted}
              onCancel={handleLifecycleClose}
            />
          ) : null}
          {showInitialLoading ? (
            <section className="master-data-page__loading">
              <p className="master-data-page__loading-text">Loading master data…</p>
            </section>
          ) : null}

          {departments.length === 0 && !isLoading ? (
            <p className="master-data-page__empty">No Departments are configured yet.</p>
          ) : null}
          {departments.length > 0 ? (
            <div className="master-data-page__table-wrapper">
              <table className="master-data-page__table">
                <thead>
                  <tr>
                    <th scope="col" data-label="Name">Name</th>
                    <th scope="col" data-label="Code">Code</th>
                    <th scope="col" data-label="Company">Company</th>
                    <th scope="col" data-label="Sort order">Sort order</th>
                    <th scope="col" data-label="Routing">Routing</th>
                    <th scope="col" data-label="Status">Status</th>
                    <th scope="col" data-label="Actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((department) => (
                    <tr
                      key={department.id}
                      className={`master-data-page__row${department.is_active ? "" : " master-data-page__row--inactive"}`}
                    >
                      <th scope="row" className="master-data-page__row-header" data-label="Name">
                        {department.name}
                      </th>
                      <td data-label="Code">{department.code}</td>
                      <td data-label="Company">{department.company_name ?? "—"}</td>
                      <td data-label="Sort order">{department.sort_order}</td>
                      <td data-label="Routing">
                        <span
                          className={`master-data-page__badge master-data-page__badge--${
                            department.is_ticket_routing_enabled ? "on" : "off"
                          }`}
                          aria-label={
                            department.is_ticket_routing_enabled
                              ? "Ticket routing enabled"
                              : "Ticket routing disabled"
                          }
                        >
                          {department.is_ticket_routing_enabled ? "Routing on" : "Routing off"}
                        </span>
                      </td>
                      <td data-label="Status">
                        <span
                          className={`master-data-page__badge master-data-page__badge--${
                            department.is_active ? "active" : "inactive"
                          }`}
                          aria-label={department.is_active ? "Active department" : "Inactive department"}
                        >
                          {department.is_active ? "Active" : "Inactive"}
                          {!department.is_active ? <span className="master-data-page__visually-hidden"> (hidden from operational lists)</span> : null}
                        </span>
                      </td>
                      <td data-label="Actions">
                        <div className="master-data-page__row-actions">
                        <button
                          type="button"
                          ref={(element) => {
                            if (element === null) {
                              editButtonRefs.current.delete(department.id);
                            } else {
                              editButtonRefs.current.set(department.id, element);
                            }
                          }}
                          className="master-data-page__row-action master-data-page__row-action--edit"
                          onClick={() => handleEditOpen(department.id)}
                          disabled={isLoading}
                          aria-label={`Edit ${department.name}`}
                          aria-expanded={editingDepartmentId === department.id}
                          aria-controls={`department-edit-panel-${department.id}`}
                        >
                          Edit
                        </button>
                        {department.is_active ? (
                          <button
                            type="button"
                            ref={(element) => {
                              if (element === null) {
                                lifecycleButtonRefs.current.delete(department.id);
                              } else {
                                lifecycleButtonRefs.current.set(department.id, element);
                              }
                            }}
                            className="master-data-page__row-action master-data-page__row-action--deactivate"
                            onClick={() => handleLifecycleOpen(department.id, "deactivate")}
                            disabled={isLoading}
                            aria-label={`Deactivate ${department.name}`}
                            aria-expanded={
                              lifecycleSelection !== null &&
                              lifecycleSelection.id === department.id &&
                              lifecycleSelection.action === "deactivate"
                            }
                            aria-controls={`department-lifecycle-panel-${department.id}-deactivate`}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            ref={(element) => {
                              if (element === null) {
                                lifecycleButtonRefs.current.delete(department.id);
                              } else {
                                lifecycleButtonRefs.current.set(department.id, element);
                              }
                            }}
                            className="master-data-page__row-action master-data-page__row-action--reactivate"
                            onClick={() => handleLifecycleOpen(department.id, "reactivate")}
                            disabled={isLoading}
                            aria-label={`Reactivate ${department.name}`}
                            aria-expanded={
                              lifecycleSelection !== null &&
                              lifecycleSelection.id === department.id &&
                              lifecycleSelection.action === "reactivate"
                            }
                            aria-controls={`department-lifecycle-panel-${department.id}-reactivate`}
                          >
                            Reactivate
                          </button>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {state.kind !== "error" && tab === "request-categories" ? (
        <section
          id="master-data-panel-request-categories"
          role="tabpanel"
          aria-labelledby="master-data-tab-request-categories"
          className="master-data-page__panel"
        >
          {showInitialLoading ? (
            <section className="master-data-page__loading">
              <p className="master-data-page__loading-text">Loading master data…</p>
            </section>
          ) : null}

          {hasLoaded ? (
            <RequestCategoryCreatePanel
              departments={eligibleRoutingDepartments}
              disabled={isLoading}
              isOpen={isRequestCategoryCreateOpen}
              onOpenChange={handleRequestCategoryCreateOpenChange}
              onCreated={handleRequestCategoryCreated}
              onCreateOpen={clearPageNotice}
            />
          ) : null}
          {selectedRequestType !== null ? (
            <RequestCategoryEditPanel
              key={selectedRequestType.id}
              requestType={selectedRequestType}
              departments={eligibleRoutingDepartments}
              disabled={isLoading}
              onUpdated={handleRequestTypeUpdated}
              onCancel={handleRequestTypeEditClose}
            />
          ) : null}
          {selectedRequestTypeLifecycle !== null && requestTypeLifecycleSelection !== null ? (
            <RequestCategoryLifecyclePanel
              key={`request-type-lifecycle-${selectedRequestTypeLifecycle.id}-${requestTypeLifecycleSelection.action}`}
              requestType={selectedRequestTypeLifecycle}
              action={requestTypeLifecycleSelection.action}
              disabled={isLoading}
              onCompleted={handleRequestTypeLifecycleCompleted}
              onCancel={handleRequestTypeLifecycleClose}
            />
          ) : null}
          {requestTypes.length === 0 && !isLoading ? (
            <p className="master-data-page__empty">No Request Categories are configured yet.</p>
          ) : null}
          {requestTypes.length > 0 ? (
            <div className="master-data-page__table-wrapper">
              <table className="master-data-page__table">
                <thead>
                  <tr>
                    <th scope="col" data-label="Name">Name</th>
                    <th scope="col" data-label="Code">Code</th>
                    <th scope="col" data-label="Description">Description</th>
                    <th scope="col" data-label="Default Department">Default Department</th>
                    <th scope="col" data-label="Sort order">Sort order</th>
                    <th scope="col" data-label="Audience">Audience</th>
                    <th scope="col" data-label="Status">Status</th>
                    <th scope="col" data-label="Actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requestTypes.map((requestType) => (
                    <tr
                      key={requestType.id}
                      className={`master-data-page__row${requestType.is_active ? "" : " master-data-page__row--inactive"}`}
                    >
                      <th scope="row" className="master-data-page__row-header" data-label="Name">
                        {requestType.name}
                      </th>
                      <td data-label="Code">{requestType.code}</td>
                      <td data-label="Description" className="master-data-page__description-cell">
                        {requestType.description ?? "—"}
                      </td>
                      <td data-label="Default Department">{requestType.default_department_name ?? "Not set"}</td>
                      <td data-label="Sort order">{requestType.sort_order}</td>
                      <td data-label="Audience">
                        <ul className="master-data-page__audience-list">
                          <li>
                            <span
                              className={`master-data-page__badge master-data-page__badge--${
                                requestType.is_internal_visible ? "on" : "off"
                              }`}
                              aria-label={
                                requestType.is_internal_visible
                                  ? "Visible to Internal audience"
                                  : "Hidden from Internal audience"
                              }
                            >
                              Internal
                            </span>
                          </li>
                          <li>
                            <span
                              className={`master-data-page__badge master-data-page__badge--${
                                requestType.is_customer_visible ? "on" : "off"
                              }`}
                              aria-label={
                                requestType.is_customer_visible
                                  ? "Visible to Customer audience"
                                  : "Hidden from Customer audience"
                              }
                            >
                              Customer
                            </span>
                          </li>
                          <li>
                            <span
                              className={`master-data-page__badge master-data-page__badge--${
                                requestType.is_partner_visible ? "on" : "off"
                              }`}
                              aria-label={
                                requestType.is_partner_visible
                                  ? "Visible to Partner audience"
                                  : "Hidden from Partner audience"
                              }
                            >
                              Partner
                            </span>
                          </li>
                        </ul>
                      </td>
                      <td data-label="Status">
                        <span
                          className={`master-data-page__badge master-data-page__badge--${
                            requestType.is_active ? "active" : "inactive"
                          }`}
                          aria-label={requestType.is_active ? "Active request category" : "Inactive request category"}
                        >
                          {requestType.is_active ? "Active" : "Inactive"}
                          {!requestType.is_active ? (
                            <span className="master-data-page__visually-hidden"> (hidden from operational lists)</span>
                          ) : null}
                        </span>
                      </td>
                      <td data-label="Actions">
                        <div className="master-data-page__row-actions">
                          <button
                            type="button"
                            ref={(element) => {
                              if (element === null) {
                                requestTypeEditButtonRefs.current.delete(requestType.id);
                              } else {
                                requestTypeEditButtonRefs.current.set(requestType.id, element);
                              }
                            }}
                            className="master-data-page__row-action master-data-page__row-action--edit"
                            onClick={() => handleRequestTypeEditOpen(requestType.id)}
                            disabled={isLoading}
                            aria-label={`Edit ${requestType.name}`}
                            aria-expanded={editingRequestTypeId === requestType.id}
                            aria-controls={`request-category-edit-panel-${requestType.id}`}
                          >
                            Edit
                          </button>
                          {requestType.is_active ? (
                            <button
                              type="button"
                              ref={(element) => {
                                if (element === null) {
                                  requestTypeLifecycleButtonRefs.current.delete(requestType.id);
                                } else {
                                  requestTypeLifecycleButtonRefs.current.set(requestType.id, element);
                                }
                              }}
                              className="master-data-page__row-action master-data-page__row-action--deactivate"
                              onClick={() => handleRequestTypeLifecycleOpen(requestType.id, "deactivate")}
                              disabled={isLoading}
                              aria-label={`Deactivate ${requestType.name}`}
                              aria-expanded={
                                requestTypeLifecycleSelection !== null &&
                                requestTypeLifecycleSelection.id === requestType.id &&
                                requestTypeLifecycleSelection.action === "deactivate"
                              }
                              aria-controls={`request-category-lifecycle-panel-${requestType.id}-deactivate`}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              ref={(element) => {
                                if (element === null) {
                                  requestTypeLifecycleButtonRefs.current.delete(requestType.id);
                                } else {
                                  requestTypeLifecycleButtonRefs.current.set(requestType.id, element);
                                }
                              }}
                              className="master-data-page__row-action master-data-page__row-action--reactivate"
                              onClick={() => handleRequestTypeLifecycleOpen(requestType.id, "reactivate")}
                              disabled={isLoading}
                              aria-label={`Reactivate ${requestType.name}`}
                              aria-expanded={
                                requestTypeLifecycleSelection !== null &&
                                requestTypeLifecycleSelection.id === requestType.id &&
                                requestTypeLifecycleSelection.action === "reactivate"
                              }
                              aria-controls={`request-category-lifecycle-panel-${requestType.id}-reactivate`}
                            >
                              Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}
    </section>
  );
}
